import React, { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Globe,
  RefreshCw,
  Search,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  CandlestickChart,
} from 'lucide-react';
import { useMarketPrices, useMarketOverview, useMarketTicker } from '@/hooks/useApi';
import { Card, LoadingSpinner, Badge } from '@/components/UI';
import { formatCurrency, formatPercent, formatNumber, pnlColor, pnlBgColor } from '@/utils';
import type { PriceInfo, MarketOverview as MarketOverviewType } from '@/types';

const DEFAULT_COINS = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT', 'ADA/USDT',
  'DOGE/USDT', 'DOT/USDT', 'AVAX/USDT', 'LINK/USDT', 'MATIC/USDT',
  'UNI/USDT', 'ATOM/USDT', 'LTC/USDT', 'BCH/USDT', 'NEAR/USDT',
  'ALGO/USDT', 'AAVE/USDT', 'SHIB/USDT', 'OP/USDT', 'ARB/USDT',
];

export function MarketPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState<string>('BTC/USDT');
  const symbolsParam = DEFAULT_COINS.join(',');

  const { data: prices, isLoading: pricesLoading, refetch: refetchPrices } = useMarketPrices(symbolsParam);
  const { data: marketOverview, isLoading: overviewLoading } = useMarketOverview();
  const { data: ticker, isLoading: tickerLoading } = useMarketTicker(selectedSymbol);

  const filteredPrices = (prices as PriceInfo[] | undefined)?.filter((p) =>
    p.symbol.toLowerCase().includes(searchQuery.toLowerCase())
  ) ?? [];

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Live Market</h1>
          <p className="text-sm text-surface-500 mt-1">
            Real-time cryptocurrency prices from Coinbase, Kraken & CoinGecko
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetchPrices()}
            className="btn-ghost flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Market Overview Cards */}
      {overviewLoading ? (
        <LoadingSpinner text="Loading market overview..." />
      ) : marketOverview ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="relative overflow-hidden">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-brand-400" />
              <span className="text-xs text-surface-500">Total Market Cap</span>
            </div>
            <p className="text-lg font-bold text-surface-100">
              {formatCurrency((marketOverview as MarketOverviewType).total_market_cap)}
            </p>
          </Card>
          <Card className="relative overflow-hidden">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-brand-400" />
              <span className="text-xs text-surface-500">24h Volume</span>
            </div>
            <p className="text-lg font-bold text-surface-100">
              {formatCurrency((marketOverview as MarketOverviewType).total_volume_24h)}
            </p>
          </Card>
          <Card className="relative overflow-hidden">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-surface-500">₿ BTC Dominance</span>
            </div>
            <p className="text-lg font-bold text-surface-100">
              {((marketOverview as MarketOverviewType).btc_dominance ?? 0).toFixed(1)}%
            </p>
          </Card>
          <Card className="relative overflow-hidden">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-surface-500">Ξ ETH Dominance</span>
            </div>
            <p className="text-lg font-bold text-surface-100">
              {((marketOverview as MarketOverviewType).eth_dominance ?? 0).toFixed(1)}%
            </p>
          </Card>
        </div>
      ) : null}

      {/* Search + Price Table */}
      <Card title="Crypto Prices" subtitle="Auto-refreshing every 15s">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
            <input
              type="text"
              placeholder="Search coins... (e.g. BTC, ETH, SOL)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>
        </div>

        {pricesLoading ? (
          <LoadingSpinner text="Loading prices..." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-800">
                  <th className="text-left py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">Coin</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">Price</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">24h Change</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider hidden md:table-cell">Market Cap</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider hidden lg:table-cell">24h Volume</th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrices.map((coin: PriceInfo) => {
                  const isPositive = coin.change_24h_pct >= 0;
                  const baseSymbol = coin.symbol.split('/')[0];
                  const isSelected = coin.symbol === selectedSymbol;

                  return (
                    <tr
                      key={coin.symbol}
                      className={`border-b border-surface-800/50 hover:bg-surface-800/30 transition-colors cursor-pointer ${
                        isSelected ? 'bg-brand-600/10 border-l-2 border-l-brand-500' : ''
                      }`}
                      onClick={() => setSelectedSymbol(coin.symbol)}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center text-xs font-bold text-surface-300">
                            {baseSymbol.slice(0, 2)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-surface-200">{baseSymbol}</p>
                            <p className="text-xs text-surface-600">{coin.symbol}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <p className="text-sm font-medium text-surface-200">
                          {formatCurrency(coin.price)}
                        </p>
                        {coin.is_stale && (
                          <p className="text-xs text-warning mt-0.5">⚠ Stale</p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${pnlBgColor(coin.change_24h_pct)}`}>
                          {isPositive ? (
                            <ArrowUpRight className="w-3 h-3" />
                          ) : (
                            <ArrowDownRight className="w-3 h-3" />
                          )}
                          {formatPercent(coin.change_24h_pct)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right hidden md:table-cell">
                        <p className="text-xs text-surface-400">
                          {coin.market_cap > 0 ? formatCurrency(coin.market_cap) : '—'}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-right hidden lg:table-cell">
                        <p className="text-xs text-surface-400">
                          {coin.volume_24h > 0 ? formatCurrency(coin.volume_24h) : '—'}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSymbol(coin.symbol);
                          }}
                          className={`text-xs px-2 py-1 rounded ${
                            isSelected
                              ? 'bg-brand-600 text-white'
                              : 'bg-surface-700 text-surface-400 hover:text-surface-200'
                          }`}
                        >
                          Detail
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredPrices.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-surface-500">
                      No coins found matching "{searchQuery}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Ticker Detail Panel */}
      {selectedSymbol && (
        <Card title={`${selectedSymbol.split('/')[0]} / USDT`} subtitle="Detailed Ticker">
          {tickerLoading ? (
            <LoadingSpinner text="Loading ticker..." />
          ) : ticker ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <p className="text-xs text-surface-500 mb-1">Price</p>
                <p className="text-sm font-bold text-surface-100">{formatCurrency(ticker.price)}</p>
              </div>
              <div>
                <p className="text-xs text-surface-500 mb-1">24h Change</p>
                <p className={`text-sm font-bold ${pnlColor(ticker.change_24h_pct)}`}>
                  {formatPercent(ticker.change_24h_pct)}
                </p>
              </div>
              <div>
                <p className="text-xs text-surface-500 mb-1">24h High</p>
                <p className="text-sm font-medium text-surface-200">
                  {ticker.high_24h > 0 ? formatCurrency(ticker.high_24h) : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-surface-500 mb-1">24h Low</p>
                <p className="text-sm font-medium text-surface-200">
                  {ticker.low_24h > 0 ? formatCurrency(ticker.low_24h) : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-surface-500 mb-1">24h Volume</p>
                <p className="text-sm font-medium text-surface-200">
                  {ticker.volume_24h > 0 ? formatCurrency(ticker.volume_24h) : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-surface-500 mb-1">Market Cap</p>
                <p className="text-sm font-medium text-surface-200">
                  {ticker.market_cap > 0 ? formatCurrency(ticker.market_cap) : '—'}
                </p>
              </div>
              {(ticker.bid > 0 || ticker.ask > 0) && (
                <>
                  <div>
                    <p className="text-xs text-surface-500 mb-1">Bid</p>
                    <p className="text-sm font-medium text-profit">{formatCurrency(ticker.bid)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-surface-500 mb-1">Ask</p>
                    <p className="text-sm font-medium text-loss">{formatCurrency(ticker.ask)}</p>
                  </div>
                </>
              )}
              <div>
                <p className="text-xs text-surface-500 mb-1">Source</p>
                <div className="flex items-center gap-2">
                  <Badge variant={ticker.is_stale ? "warning" : "profit"}>{ticker.source}</Badge>
                  {ticker.is_stale && (
                    <Badge variant="warning">STALE</Badge>
                  )}
                </div>
                {ticker.last_updated ? (
                  <p className={`text-xs mt-1 ${ticker.is_stale ? 'text-warning' : 'text-surface-600'}`}>
                    {ticker.is_stale ? '⚠' : '✓'} Live price from {ticker.source}, updated {new Date(ticker.last_updated).toLocaleTimeString()}
                  </p>
                ) : (
                  <p className="text-xs text-surface-600 mt-1">Price source: {ticker.source || 'unknown'}</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-surface-500">No ticker data available for {selectedSymbol}</p>
          )}
        </Card>
      )}

      {/* Top Movers */}
      {filteredPrices.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="🔥 Top Gainers" subtitle="24h change">
            <div className="space-y-2">
              {[...filteredPrices]
                .sort((a: PriceInfo, b: PriceInfo) => b.change_24h_pct - a.change_24h_pct)
                .slice(0, 5)
                .map((coin: PriceInfo) => (
                  <div
                    key={coin.symbol}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-800/30 hover:bg-surface-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-profit" />
                      <span className="text-sm font-medium text-surface-200">{coin.symbol}</span>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-bold ${pnlColor(coin.change_24h_pct)}`}>
                        {formatPercent(coin.change_24h_pct)}
                      </span>
                      <span className="text-xs text-surface-500 ml-2">{formatCurrency(coin.price)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </Card>
          <Card title="📉 Top Losers" subtitle="24h change">
            <div className="space-y-2">
              {[...filteredPrices]
                .sort((a: PriceInfo, b: PriceInfo) => a.change_24h_pct - b.change_24h_pct)
                .slice(0, 5)
                .map((coin: PriceInfo) => (
                  <div
                    key={coin.symbol}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-800/30 hover:bg-surface-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-loss" />
                      <span className="text-sm font-medium text-surface-200">{coin.symbol}</span>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-bold ${pnlColor(coin.change_24h_pct)}`}>
                        {formatPercent(coin.change_24h_pct)}
                      </span>
                      <span className="text-xs text-surface-500 ml-2">{formatCurrency(coin.price)}</span>
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
